import React from 'react';
import { ITemporalJoinConditionDraftData } from '../../../../redux/slices/dataModeler';
import {
  FieldType,
  ITableColumnFieldsFragment,
  TemporalJoinType,
} from '../../../../utils/apollo/graphqlSchema';
import {
  JoinConditionCell,
  JoinConditionDimSection,
  JoinConditionRow,
  StyledJoinTypeSelect,
  StyledTemporalJoinTypeSelect,
} from './DataModelerJoinUI';
import { temporalJoinTypeConfig } from '../utils';
import {
  DataModelerSelectSchemaFieldKey,
  DataModelerSelectTableColumn,
} from '../DataModelerFieldDropdown';
import { JOIN_TYPE_SELECT_OPTS, JoinTypeOption } from './utils';
import { dataModelTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import { SquareRemoveButton } from '../../../../components/Button';

interface IProps {
  isEditMode: boolean;
  factViewKey: string;
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
  temporalCondition: ITemporalJoinConditionDraftData;
  setTemporalCondition: (
    temporalCondition: ITemporalJoinConditionDraftData | null,
  ) => void;
  setDraftJoinOption: (newOption: JoinTypeOption) => void;
  isLast: boolean;
}

const temporalJoinSelectOpts = Object.values(TemporalJoinType).map(
  (joinType) => {
    return {
      text: joinType,
      value: joinType,
    };
  },
);

const DataModelerEditTemporalCondition = ({
  isEditMode,
  viewKey,
  tableKey,
  availableColumnsFromServer,
  temporalCondition,
  setTemporalCondition,
  factViewKey,
  setDraftJoinOption,
  isLast,
}: IProps) => {
  const handleJoinTypeChange = (newJoinTypeStr: string) => {
    const newJoinType = newJoinTypeStr as TemporalJoinType;
    const { numOps: newNumOps } = temporalJoinTypeConfig[newJoinType];
    const factTemporalKey =
      newNumOps > 0 ? temporalCondition.factTemporalKey : null;
    const dimSecondTemporalColumn =
      newNumOps === 2 ? temporalCondition.dimSecondTemporalColumn : null;
    setTemporalCondition({
      ...temporalCondition,
      joinType: newJoinType,
      factTemporalKey,
      dimSecondTemporalColumn,
    });
  };

  const handleSetDimTemporalColumn = (
    tableColumns: ITableColumnFieldsFragment,
  ) => {
    setTemporalCondition({
      ...temporalCondition,
      dimTemporalColumn: tableColumns.column,
    });
  };

  const handleSetDimSecondTemporalColumn = (
    tableColumns: ITableColumnFieldsFragment,
  ) => {
    setTemporalCondition({
      ...temporalCondition,
      dimSecondTemporalColumn: tableColumns.column,
    });
  };

  const handleSetFactTemporalKey = (fieldKey: string) => {
    setTemporalCondition({
      ...temporalCondition,
      factTemporalKey: fieldKey,
    });
  };

  const temporalJoinType = temporalCondition.joinType;
  const { numOps } = temporalJoinTypeConfig[temporalJoinType];

  const dimensionColumnLabel = numOps < 2 ? 'Time Column' : 'Start Time Column';

  const onSwitchType = (newType: string) => {
    setTemporalCondition(null);
    setDraftJoinOption(newType as JoinTypeOption);
  };

  return (
    <JoinConditionRow $isLast={isLast}>
      <JoinConditionCell>
        <StyledJoinTypeSelect
          disabled={!isEditMode}
          options={JOIN_TYPE_SELECT_OPTS}
          value={JOIN_TYPE_SELECT_OPTS[JOIN_TYPE_SELECT_OPTS.length - 1].value}
          onChange={onSwitchType}
        />
      </JoinConditionCell>
      {numOps > 0 ? (
        <DataModelerSelectSchemaFieldKey
          isDisabled={!isEditMode}
          viewKey={factViewKey}
          fieldKey={temporalCondition.factTemporalKey ?? undefined}
          setFieldKey={handleSetFactTemporalKey}
          fieldTypeFilter={[FieldType.Timestamp, FieldType.Date]}
        />
      ) : (
        <JoinConditionCell>N/A</JoinConditionCell>
      )}
      <JoinConditionCell>
        <StyledTemporalJoinTypeSelect
          disabled={!isEditMode}
          options={temporalJoinSelectOpts}
          value={temporalCondition.joinType}
          onChange={handleJoinTypeChange}
        />
      </JoinConditionCell>
      <JoinConditionDimSection>
        <DataModelerSelectTableColumn
          isDisabled={!isEditMode}
          viewKey={viewKey}
          tableKey={tableKey}
          availableColumnsFromServer={availableColumnsFromServer}
          columnName={temporalCondition.dimTemporalColumn ?? undefined}
          setColumn={handleSetDimTemporalColumn}
          addFieldOnSetColumn={true}
          placeholder={dimensionColumnLabel}
          fieldTypeFilter={[FieldType.Timestamp, FieldType.Date]}
        />
        {numOps === 2 && (
          <DataModelerSelectTableColumn
            isDisabled={!isEditMode}
            viewKey={viewKey}
            tableKey={tableKey}
            availableColumnsFromServer={availableColumnsFromServer}
            columnName={temporalCondition.dimSecondTemporalColumn ?? undefined}
            setColumn={handleSetDimSecondTemporalColumn}
            addFieldOnSetColumn={true}
            placeholder={'End Time Column'}
            fieldTypeFilter={[FieldType.Timestamp, FieldType.Date]}
          />
        )}
        {isEditMode && (
          <SquareRemoveButton
            onClick={() => setTemporalCondition(null)}
            content={dataModelTooltipOptions.removeJoinConditionIcon.content}
          />
        )}
      </JoinConditionDimSection>
    </JoinConditionRow>
  );
};

export default DataModelerEditTemporalCondition;
